import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, SouthWestOutlined,
    CheckCircleOutline, LockOutlined, AccountBalanceOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
};

const LINKED_BANKS = [
    { name: 'Access Bank', acct: '··· 7821', icon: 'A' },
    { name: 'GTBank',      acct: '··· 4419', icon: 'G' },
    { name: 'Zenith Bank', acct: '··· 9930', icon: 'Z' },
];

const BANKS_NEW      = ['First Bank', 'UBA', 'Kuda Bank', 'Opay', 'Palmpay', 'Stanbic IBTC'];
const QUICK_AMOUNTS  = [5000, 10000, 20000, 50000, 100000];
const WALLET_BALANCE = 125000;

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const WithdrawPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [step,         setStep]         = useState<1 | 2 | 3>(1);
    const [amount,       setAmount]       = useState('');
    const [selectedBank, setSelectedBank] = useState<typeof LINKED_BANKS[0] | null>(null);
    const [newBank,      setNewBank]      = useState('');
    const [loading,      setLoading]      = useState(false);
    const [balance,      setBalance]      = useState(WALLET_BALANCE);

    const numAmount    = parseFloat(amount) || 0;
    const insufficient = numAmount > balance;

    // ── Theme tokens ──
    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg,
        border: surfaceBdr,
        boxShadow: surfaceShd,
        borderRadius: '20px',
        backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleNext = () => {
        if (step === 1 && (selectedBank || newBank)) setStep(2);
        else if (step === 2 && numAmount > 0 && !insufficient) {
            setLoading(true);
            setTimeout(() => { setBalance(b => b - numAmount); setLoading(false); setStep(3); }, 1800);
        }
    };

    const goBack = () => {
        if (step === 1) navigate(-1);
        else setStep(prev => (prev - 1) as any);
    };

    const destinationLabel = selectedBank ? `${selectedBank.name} ${selectedBank.acct}` : newBank;

    /* ── SUCCESS ── */
    if (step === 3) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Withdrawal Sent!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{fmt(numAmount)} is on its way to</Typography>
                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1rem', mb: .25 }}>{destinationLabel}</Typography>
                <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 1 }}>Typically arrives within minutes</Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 20, px: 2, py: .6, mb: 4 }}>
                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>New Balance: {fmt(balance)}</Typography>
                </Box>
                <Box display="block">
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 5, py: 1.4, textTransform: 'none', fontWeight: 700, fontSize: '.9rem', boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Back to Wallet
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.red, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            Step {step} of 2
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Withdraw
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>Bal: {fmt(balance)}</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* STEP 1: Select bank */}
                {step === 1 && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2.5 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Linked Accounts</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {LINKED_BANKS.map((b, i) => (
                                    <Box key={i} onClick={() => { setSelectedBank(selectedBank?.name === b.name ? null : b); setNewBank(''); }} sx={{
                                        display: 'flex', alignItems: 'center', gap: 2, p: 1.75, borderRadius: '14px', cursor: 'pointer',
                                        background: selectedBank?.name === b.name ? alpha(B.red, .08) : 'transparent',
                                        border: `1px solid ${selectedBank?.name === b.name ? alpha(B.red, .3) : dividerC}`,
                                        transition: 'all .18s',
                                    }}>
                                        <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: alpha(B.purple, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Instrument Serif', serif", color: textPri, fontSize: '1.1rem', flexShrink: 0 }}>
                                            {b.icon}
                                        </Box>
                                        <Box flex={1}>
                                            <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.85rem' }}>{b.name}</Typography>
                                            <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{b.acct}</Typography>
                                        </Box>
                                        <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selectedBank?.name === b.name ? B.red : dividerC}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {selectedBank?.name === b.name && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: B.red }} />}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {!selectedBank && (
                            <Box sx={{ ...glassCard, p: 2.5, mb: 2.5 }}>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Other Bank</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                    {BANKS_NEW.map(b => (
                                        <Box key={b} onClick={() => setNewBank(newBank === b ? '' : b)} sx={{
                                            p: 1.25, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                                            background: newBank === b ? alpha(B.red, .08) : alpha(isDark ? '#fff' : B.dark, .03),
                                            border: `1px solid ${newBank === b ? alpha(B.red, .35) : dividerC}`,
                                            transition: 'all .18s',
                                        }}>
                                            <Typography sx={{ color: newBank === b ? B.red : textMid, fontSize: '.73rem', fontWeight: 600 }}>{b}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {/* STEP 2: Amount */}
                {step === 2 && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, p: 1.5, ...glassCard }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: '50%', background: alpha(B.red, .1), border: `1px solid ${alpha(B.red, .25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <AccountBalanceOutlined sx={{ color: B.red, fontSize: 18 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.83rem' }}>{destinationLabel}</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>Destination account</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ ...glassCard, p: 3, mb: 2.5 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>How Much?</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{ color: textFaint, fontFamily: "'Instrument Serif', serif", fontSize: '2rem' }}>₦</Typography>
                                <Box component="input" value={amount}
                                     onChange={e => setAmount((e.target as HTMLInputElement).value.replace(/[^0-9]/g, ''))}
                                     placeholder="0" autoFocus
                                     sx={{ background: 'transparent', border: 'none', outline: 'none', color: insufficient ? B.red : textPri, fontFamily: "'Instrument Serif', serif", fontSize: { xs: '2.6rem', sm: '3.2rem' }, fontWeight: 400, letterSpacing: '-.03em', width: '100%', '&::placeholder': { color: textFaint }, transition: 'color .2s' }} />
                            </Box>
                            <Box sx={{ height: '1px', background: `linear-gradient(90deg, ${insufficient ? B.red : B.purple}, ${alpha(isDark ? '#fff' : B.dark, .08)})`, mb: 1.5 }} />

                            {insufficient && (
                                <Typography sx={{ color: B.red, fontSize: '.74rem', fontWeight: 600, mb: 1.5 }}>
                                    ⚠ Exceeds available balance of {fmt(balance)}
                                </Typography>
                            )}

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {QUICK_AMOUNTS.filter(q => q <= balance).map(q => (
                                    <Chip key={q} label={`₦${q.toLocaleString()}`} onClick={() => setAmount(String(q))}
                                          sx={{ background: amount === String(q) ? alpha(B.purple, .15) : alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${amount === String(q) ? B.purple : dividerC}`, color: amount === String(q) ? B.purple : textMid, fontWeight: 600, fontSize: '.72rem', cursor: 'pointer', '&:hover': { background: alpha(B.purple, .1) } }} />
                                ))}
                                <Chip label="Max" onClick={() => setAmount(String(balance))}
                                      sx={{ background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .25)}`, color: B.green, fontWeight: 700, fontSize: '.72rem', cursor: 'pointer' }} />
                            </Box>
                        </Box>
                    </>
                )}

                {/* CTA */}
                {(
                    (step === 1 && (selectedBank || newBank)) ||
                    (step === 2 && numAmount > 0 && !insufficient)
                ) && (
                    <Button fullWidth onClick={handleNext} disabled={loading} sx={{
                        background: loading ? alpha(B.red, .4) : `linear-gradient(135deg, ${B.red} 0%, #FF7A91 100%)`,
                        color: '#fff', borderRadius: 50, py: 1.8, textTransform: 'none',
                        fontWeight: 700, fontSize: '.95rem',
                        boxShadow: `0 14px 36px ${alpha(B.red, .3)}`,
                        transition: 'all .25s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 20px 44px ${alpha(B.red, .4)}` },
                        '&:disabled': { color: alpha('#fff', .35) },
                    }} startIcon={loading ? undefined : <SouthWestOutlined />}>
                        {loading ? 'Processing…' :
                            step === 1 ? 'Next — Enter Amount' :
                                `Withdraw ${fmt(numAmount)}`}
                    </Button>
                )}

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.6rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.06em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default WithdrawPage;